import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  Copy,
  LayoutGrid,
  MonitorPlay,
  Pencil,
  Plus,
  Save,
  Search,
  Sparkles,
  X,
} from 'lucide-react';

import { adminContent } from 'services/api';
import type { AdminInspireCard, AdminInspireDeck } from 'services/api/adminContentClient';
import type { OrganizationSummary } from 'types';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from 'components/ui/sheet';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'components/ui/use-toast';
import InspireCallPreview from './InspireCallPreview';

const normalizeList = <T,>(payload: any): T[] => Array.isArray(payload) ? payload : payload?.results || [];
const selectClassName = 'mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm';

type InspireCardDraft = {
  id: string;
  prompt: string;
  helper_text: string;
  facilitator_notes: string;
  status: AdminInspireCard['status'];
  media_asset: string;
};

const emptyCard = (): InspireCardDraft => ({
  id: '', prompt: '', helper_text: '', facilitator_notes: '', status: 'draft', media_asset: '',
});

const cardDraftFrom = (card: AdminInspireCard): InspireCardDraft => ({
  id: card.id,
  prompt: card.prompt,
  helper_text: card.helper_text || '',
  facilitator_notes: card.facilitator_notes || '',
  status: card.status,
  media_asset: card.media_asset || '',
});

const orderedIdsFor = (deck: AdminInspireDeck | null) => deck
  ? [...deck.items]
    .sort((left, right) => left.order - right.order)
    .filter((item) => item.card.status !== 'archived')
    .map((item) => item.card.id)
  : [];

function InspireCardsAdmin() {
  const { addToast } = useToast();
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [scope, setScope] = useState('global');
  const [decks, setDecks] = useState<AdminInspireDeck[]>([]);
  const [cards, setCards] = useState<AdminInspireCard[]>([]);
  const [selectedDeckId, setSelectedDeckId] = useState('');
  const [deckDraft, setDeckDraft] = useState({
    name: '', description: '', helper_text: '', facilitator_notes: '', accent_color: '#1F3A5F',
    status: 'draft' as AdminInspireDeck['status'],
  });
  const [orderedCardIds, setOrderedCardIds] = useState<string[]>([]);
  const [cardDraft, setCardDraft] = useState(emptyCard());
  const [cardLibraryOpen, setCardLibraryOpen] = useState(false);
  const [cardEditorOpen, setCardEditorOpen] = useState(false);
  const [cardSearch, setCardSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [workspaceView, setWorkspaceView] = useState<'authoring' | 'preview'>('authoring');
  const hydratedDeckIdRef = useRef<string | null>(null);

  const selectedDeck = useMemo(
    () => decks.find((deck) => deck.id === selectedDeckId) || null,
    [decks, selectedDeckId],
  );
  const organizationId = scope === 'global' ? null : scope;
  const brandingName = scope === 'global'
    ? 'WiseFriend'
    : organizations.find((organization) => String(organization.id) === scope)?.name || 'Organization';

  const cardById = useMemo(() => {
    const result = new Map(cards.map((card) => [card.id, card]));
    selectedDeck?.items.forEach((item) => {
      if (!result.has(item.card.id)) result.set(item.card.id, item.card);
    });
    return result;
  }, [cards, selectedDeck]);

  const orderedCards = useMemo(
    () => orderedCardIds.map((id) => cardById.get(id)).filter((card): card is AdminInspireCard => Boolean(card)),
    [cardById, orderedCardIds],
  );

  const availableCards = useMemo(() => {
    const query = cardSearch.trim().toLocaleLowerCase();
    return cards
      .filter((card) => card.status !== 'archived' && !orderedCardIds.includes(card.id))
      .filter((card) => !query || card.prompt.toLocaleLowerCase().includes(query))
      .sort((left, right) => left.prompt.localeCompare(right.prompt));
  }, [cardSearch, cards, orderedCardIds]);

  const deckHasChanges = useMemo(() => {
    if (!selectedDeck) return false;
    return deckDraft.name !== selectedDeck.name
      || deckDraft.description !== selectedDeck.description
      || deckDraft.helper_text !== (selectedDeck.helper_text || '')
      || deckDraft.facilitator_notes !== (selectedDeck.facilitator_notes || '')
      || deckDraft.accent_color.toUpperCase() !== selectedDeck.accent_color.toUpperCase()
      || deckDraft.status !== selectedDeck.status
      || JSON.stringify(orderedCardIds) !== JSON.stringify(orderedIdsFor(selectedDeck));
  }, [deckDraft, orderedCardIds, selectedDeck]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requests: Promise<any>[] = [
        adminContent.inspireDecks.list(scope),
        adminContent.inspireCards.list('global'),
      ];
      if (scope !== 'global') requests.push(adminContent.inspireCards.list(scope));
      const responses = await Promise.all(requests);
      const nextDecks = normalizeList<AdminInspireDeck>(responses[0].data);
      const globalCards = normalizeList<AdminInspireCard>(responses[1].data);
      const organizationCards = responses[2] ? normalizeList<AdminInspireCard>(responses[2].data) : [];
      setDecks(nextDecks);
      setCards([...globalCards, ...organizationCards].filter(
        (card, index, all) => all.findIndex((item) => item.id === card.id) === index,
      ));
      setSelectedDeckId((current) => nextDecks.some((deck) => deck.id === current) ? current : nextDecks[0]?.id || '');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to load Inspire Card content.');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    void adminContent.organizations.list({ is_active: true })
      .then((response) => setOrganizations(normalizeList(response.data)))
      .catch(() => setOrganizations([]));
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const nextDeckId = selectedDeck?.id || null;
    if (hydratedDeckIdRef.current === nextDeckId) return;
    hydratedDeckIdRef.current = nextDeckId;
    setCardLibraryOpen(false);
    setCardEditorOpen(false);
    setCardSearch('');
    setCardDraft(emptyCard());
    if (!selectedDeck) {
      setDeckDraft({
        name: '', description: '', helper_text: '', facilitator_notes: '', accent_color: '#1F3A5F', status: 'draft',
      });
      setOrderedCardIds([]);
      return;
    }
    setDeckDraft({
      name: selectedDeck.name,
      description: selectedDeck.description,
      helper_text: selectedDeck.helper_text || '',
      facilitator_notes: selectedDeck.facilitator_notes || '',
      accent_color: selectedDeck.accent_color || '#1F3A5F',
      status: selectedDeck.status,
    });
    setOrderedCardIds(orderedIdsFor(selectedDeck));
  }, [selectedDeck]);

  const handleScopeChange = (nextScope: string) => {
    hydratedDeckIdRef.current = null;
    setScope(nextScope);
    setSelectedDeckId('');
    setCardDraft(emptyCard());
    setCardLibraryOpen(false);
    setCardEditorOpen(false);
  };

  const selectDeck = (deckId: string) => {
    setSelectedDeckId(deckId);
    setCardLibraryOpen(false);
    setCardEditorOpen(false);
  };

  const createDeck = async () => {
    setSaving(true);
    try {
      const response = await adminContent.inspireDecks.create({
        name: `New ${scope === 'global' ? 'WiseFriend' : 'organization'} deck`,
        description: '', helper_text: '', facilitator_notes: '', accent_color: '#1F3A5F',
        status: 'draft', owner_organization_id: organizationId,
      });
      await load();
      hydratedDeckIdRef.current = null;
      setSelectedDeckId(response.data.id);
      addToast({ title: 'Inspire deck created' });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to create deck.');
    } finally {
      setSaving(false);
    }
  };

  const saveDeck = async () => {
    if (!selectedDeck || !deckDraft.name.trim()) return;
    setSaving(true);
    try {
      await adminContent.inspireDecks.update(selectedDeck.id, { ...deckDraft, name: deckDraft.name.trim() });
      await adminContent.inspireDecks.setCards(selectedDeck.id, orderedCardIds);
      await load();
      addToast({ title: 'Inspire deck saved' });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to save deck.');
    } finally {
      setSaving(false);
    }
  };

  const openNewCard = () => {
    setCardDraft(emptyCard());
    setCardLibraryOpen(false);
    setCardEditorOpen(true);
  };

  const openCardEditor = (card: AdminInspireCard) => {
    setCardDraft(cardDraftFrom(card));
    setCardLibraryOpen(false);
    setCardEditorOpen(true);
  };

  const isCardEditable = (card: AdminInspireCard) => organizationId
    ? String(card.owner_organization?.id || '') === organizationId
    : !card.owner_organization;

  const mergeSavedCard = (savedCard: AdminInspireCard) => {
    setCards((current) => {
      const existing = current.some((card) => card.id === savedCard.id);
      return existing
        ? current.map((card) => card.id === savedCard.id ? savedCard : card)
        : [...current, savedCard];
    });
    setDecks((current) => current.map((deck) => ({
      ...deck,
      items: deck.items.map((item) => item.card.id === savedCard.id ? { ...item, card: savedCard } : item),
    })));
  };

  const saveCard = async () => {
    if (!cardDraft.prompt.trim()) return;
    setSaving(true);
    const isNew = !cardDraft.id;
    const payload = {
      prompt: cardDraft.prompt.trim(),
      helper_text: cardDraft.helper_text,
      facilitator_notes: cardDraft.facilitator_notes,
      status: cardDraft.status,
      owner_organization_id: organizationId,
      media_asset: cardDraft.media_asset || null,
    };
    try {
      const response = cardDraft.id
        ? await adminContent.inspireCards.update(cardDraft.id, payload)
        : await adminContent.inspireCards.create(payload);
      const savedCard = response.data as AdminInspireCard;
      mergeSavedCard(savedCard);
      if (isNew && selectedDeck) {
        setOrderedCardIds((current) => current.includes(savedCard.id) ? current : [...current, savedCard.id]);
      }
      setCardDraft(emptyCard());
      setCardEditorOpen(false);
      addToast({
        title: isNew ? 'Inspire Card created' : 'Inspire Card saved',
        description: isNew && selectedDeck ? 'The new card was added to this deck. Save the deck to publish its order.' : undefined,
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to save card.');
    } finally {
      setSaving(false);
    }
  };

  const customizeCard = async (card: AdminInspireCard) => {
    if (!organizationId) return;
    setSaving(true);
    try {
      const response = await adminContent.inspireCards.customize(card.id, organizationId);
      const customized = response.data as AdminInspireCard;
      mergeSavedCard(customized);
      setOrderedCardIds((current) => current.map((id) => id === card.id ? customized.id : id));
      setCardDraft(cardDraftFrom(customized));
      setCardLibraryOpen(false);
      setCardEditorOpen(true);
      addToast({ title: 'Organization copy created', description: 'This deck now uses the editable organization copy.' });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to customize this card.');
    } finally {
      setSaving(false);
    }
  };

  const archiveCard = async () => {
    if (!cardDraft.id) return;
    setSaving(true);
    try {
      await adminContent.inspireCards.archive(cardDraft.id);
      setCards((current) => current.map((card) => card.id === cardDraft.id ? { ...card, status: 'archived' } : card));
      setOrderedCardIds((current) => current.filter((id) => id !== cardDraft.id));
      setCardDraft(emptyCard());
      setCardEditorOpen(false);
      addToast({ title: 'Inspire Card archived' });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to archive this card.');
    } finally {
      setSaving(false);
    }
  };

  const archiveDeck = async () => {
    if (!selectedDeck) return;
    setSaving(true);
    try {
      await adminContent.inspireDecks.archive(selectedDeck.id);
      hydratedDeckIdRef.current = null;
      await load();
      addToast({ title: 'Inspire deck archived' });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to archive this deck.');
    } finally {
      setSaving(false);
    }
  };

  const addCardToDeck = (cardId: string) => {
    setOrderedCardIds((current) => current.includes(cardId) ? current : [...current, cardId]);
  };
  const removeCardFromDeck = (cardId: string) => {
    setOrderedCardIds((current) => current.filter((id) => id !== cardId));
  };
  const moveCard = (index: number, direction: -1 | 1) => setOrderedCardIds((current) => {
    const target = index + direction;
    if (target < 0 || target >= current.length) return current;
    const next = [...current];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });

  const editingCard = cardDraft.id ? cardById.get(cardDraft.id) || null : null;

  return (
    <div className="space-y-6" data-testid="inspire-cards-admin">
      <div className="rounded-2xl border border-border/70 bg-white/60 p-4 shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-2xl font-semibold">
              <Sparkles className="h-5 w-5 text-foundation-orange-500" />
              Inspire Cards
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Build decks, set shared guidance, and arrange the cards facilitators can use in meetings.
            </p>
          </div>
          <div className="flex w-full flex-wrap items-end gap-3 sm:w-auto">
            <div className="flex rounded-full border bg-muted/30 p-1" aria-label="Inspire Cards workspace view">
              <Button
                type="button"
                size="auto"
                variant={workspaceView === 'authoring' ? 'default' : 'ghost'}
                aria-pressed={workspaceView === 'authoring'}
                onClick={() => setWorkspaceView('authoring')}
                className="min-h-9 px-4 py-2"
              >
                <LayoutGrid className="h-4 w-4" />Authoring
              </Button>
              <Button
                type="button"
                size="auto"
                variant={workspaceView === 'preview' ? 'default' : 'ghost'}
                aria-pressed={workspaceView === 'preview'}
                onClick={() => setWorkspaceView('preview')}
                className="min-h-9 px-4 py-2"
              >
                <MonitorPlay className="h-4 w-4" />Call preview
              </Button>
            </div>
            <div className="min-w-[15rem] flex-1 sm:flex-none">
              <Label htmlFor="inspire-scope">Content scope</Label>
              <select
                id="inspire-scope"
                value={scope}
                onChange={(event) => handleScopeChange(event.target.value)}
                className={selectClassName}
              >
                <option value="global">WiseFriend</option>
                {organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>{organization.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {loading ? <p className="py-12 text-center text-sm text-muted-foreground">Loading Inspire content…</p> : (
        workspaceView === 'preview'
          ? <InspireCallPreview decks={decks} brandingName={brandingName} />
          : (
            <div className="grid items-start gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
              <Card
                variant="white"
                maxWidth="none"
                padding="compact"
                disableAnimation
                className="border border-border/70 shadow-sm lg:sticky lg:top-4"
              >
                <CardHeader className="border-b border-border/70 px-2 pb-4 pt-2">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle size="small">Decks</CardTitle>
                      <p className="mt-1 text-xs font-normal text-muted-foreground">{brandingName} · {decks.length} total</p>
                    </div>
                    <Button type="button" size="auto" onClick={createDeck} disabled={saving} className="min-h-9 px-3 py-2">
                      <Plus className="h-4 w-4" />New
                    </Button>
                  </div>
                </CardHeader>
                <CardContent variant="custom" className="max-h-[70vh] space-y-2 overflow-y-auto px-2 pt-4">
                  {decks.length === 0 ? (
                    <div className="rounded-xl border border-dashed p-4 text-center">
                      <p className="text-sm font-medium">No decks in this scope</p>
                      <p className="mt-1 text-xs text-muted-foreground">Create one to start arranging cards.</p>
                    </div>
                  ) : decks.map((deck) => (
                    <button
                      type="button"
                      key={deck.id}
                      onClick={() => selectDeck(deck.id)}
                      className={`w-full rounded-xl border p-3 text-left transition-colors ${
                        deck.id === selectedDeckId
                          ? 'border-foundation-orange-400 bg-foundation-orange-50 shadow-sm'
                          : 'border-border bg-white hover:border-foundation-calm-blue-300 hover:bg-muted/30'
                      }`}
                    >
                      <span className="flex items-start gap-2.5">
                        <span
                          aria-hidden="true"
                          className="mt-0.5 h-4 w-4 shrink-0 rounded-md border border-black/10 shadow-sm"
                          style={{ backgroundColor: deck.accent_color }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold leading-5 text-foreground">{deck.name}</span>
                          <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs capitalize text-muted-foreground">
                            <span>{deck.items.filter((item) => item.card.status !== 'archived').length} cards</span>
                            <span aria-hidden="true">·</span>
                            <span>{deck.status}</span>
                          </span>
                        </span>
                      </span>
                    </button>
                  ))}
                </CardContent>
              </Card>

              {selectedDeck ? (
                <div className="min-w-0 space-y-6">
                  <Card variant="white" maxWidth="none" disableAnimation className="border border-border/70 shadow-sm">
                    <CardHeader className="border-b border-border/70 pb-4">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <CardTitle>Deck settings</CardTitle>
                            <Badge variant="outline" className="capitalize">{deckDraft.status}</Badge>
                            {deckHasChanges && <Badge className="border-amber-200 bg-amber-50 text-amber-800">Unsaved changes</Badge>}
                          </div>
                          <p className="mt-1 text-sm font-normal text-muted-foreground">Defaults here are used only when an individual card leaves the same field blank.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" size="auto" variant="ghost" onClick={archiveDeck} disabled={saving} className="min-h-9 px-3 py-2 text-red-700">
                            <Archive className="h-4 w-4" />Archive
                          </Button>
                          <Button type="button" size="auto" onClick={saveDeck} disabled={saving || !deckDraft.name.trim()} className="min-h-9 px-4 py-2">
                            <Save className="h-4 w-4" />{saving ? 'Saving…' : 'Save deck'}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent variant="custom" className="space-y-6 pt-5">
                      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_12rem]">
                        <div>
                          <Label htmlFor="deck-name">Deck name</Label>
                          <Input id="deck-name" className="mt-1" value={deckDraft.name} onChange={(event) => setDeckDraft((current) => ({ ...current, name: event.target.value }))} />
                        </div>
                        <div>
                          <Label htmlFor="deck-status">Status</Label>
                          <select id="deck-status" value={deckDraft.status} onChange={(event) => setDeckDraft((current) => ({ ...current, status: event.target.value as AdminInspireDeck['status'] }))} className={selectClassName}>
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                            <option value="archived">Archived</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_12rem]">
                        <div>
                          <Label htmlFor="deck-description">Description</Label>
                          <Textarea id="deck-description" className="mt-1 min-h-[96px]" value={deckDraft.description} onChange={(event) => setDeckDraft((current) => ({ ...current, description: event.target.value }))} placeholder="What is this deck intended to help facilitators explore?" />
                        </div>
                        <div>
                          <Label htmlFor="deck-accent-color">Accent color</Label>
                          <div className="mt-1 flex h-[42px] items-center gap-3 rounded-md border border-input bg-background px-2">
                            <Input id="deck-accent-color" type="color" value={deckDraft.accent_color} onChange={(event) => setDeckDraft((current) => ({ ...current, accent_color: event.target.value.toUpperCase() }))} className="h-8 w-12 cursor-pointer border-0 p-0" />
                            <span className="font-mono text-xs text-muted-foreground">{deckDraft.accent_color.toUpperCase()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-border/70 pt-5">
                        <h3 className="text-sm font-semibold">Deck-level guidance</h3>
                        <p className="mt-1 text-xs text-muted-foreground">Keep this broad enough to work across every card in the deck.</p>
                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                          <div>
                            <Label htmlFor="deck-helper">Default participant helper text</Label>
                            <Textarea id="deck-helper" className="mt-1 min-h-[112px]" value={deckDraft.helper_text} onChange={(event) => setDeckDraft((current) => ({ ...current, helper_text: event.target.value }))} placeholder="Shown when a card does not provide its own helper text." />
                            <p className="mt-1 text-xs text-muted-foreground">Visible to participants.</p>
                          </div>
                          <div>
                            <Label htmlFor="deck-notes">Default facilitator notes</Label>
                            <Textarea id="deck-notes" className="mt-1 min-h-[112px]" value={deckDraft.facilitator_notes} onChange={(event) => setDeckDraft((current) => ({ ...current, facilitator_notes: event.target.value }))} placeholder="Private notes used when a card does not provide its own." />
                            <p className="mt-1 text-xs text-muted-foreground">Visible only in presenter controls.</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card variant="white" maxWidth="none" disableAnimation className="border border-border/70 shadow-sm">
                    <CardHeader className="border-b border-border/70 pb-4">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <CardTitle>Cards in this deck</CardTitle>
                            <Badge variant="secondary">{orderedCards.length}</Badge>
                          </div>
                          <p className="mt-1 text-sm font-normal text-muted-foreground">Only this deck’s cards are shown here. Reorder them or open the library when you need another.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" size="auto" variant="secondary" onClick={() => setCardLibraryOpen(true)} className="min-h-9 px-3 py-2">
                            <Search className="h-4 w-4" />Add from library
                          </Button>
                          <Button type="button" size="auto" onClick={openNewCard} className="min-h-9 px-3 py-2">
                            <Plus className="h-4 w-4" />New card
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent variant="custom" className="pt-4">
                      {orderedCards.length === 0 ? (
                        <div className="rounded-2xl border border-dashed bg-muted/20 px-6 py-10 text-center">
                          <Sparkles className="mx-auto h-6 w-6 text-muted-foreground" />
                          <p className="mt-3 text-sm font-semibold">This deck is empty</p>
                          <p className="mt-1 text-sm text-muted-foreground">Add an existing card or create the first one for this deck.</p>
                          <div className="mt-4 flex flex-wrap justify-center gap-2">
                            <Button type="button" size="auto" variant="secondary" onClick={() => setCardLibraryOpen(true)} className="px-4 py-2">Add from library</Button>
                            <Button type="button" size="auto" onClick={openNewCard} className="px-4 py-2">Create a card</Button>
                          </div>
                        </div>
                      ) : (
                        <ol className="space-y-2" aria-label="Cards in this deck">
                          {orderedCards.map((card, index) => (
                            <li key={card.id} className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 rounded-xl border border-border/80 bg-white p-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center">
                              <span className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold shadow-sm" style={{ backgroundColor: deckDraft.accent_color, color: '#172033' }}>{index + 1}</span>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold leading-5 text-foreground">{card.prompt}</p>
                                <p className="mt-1 text-xs capitalize text-muted-foreground">{card.owner_organization?.name || 'WiseFriend'} · v{card.version} · {card.status}</p>
                              </div>
                              <div className="col-span-2 flex flex-wrap items-center justify-end gap-1 sm:col-span-1 sm:flex-nowrap">
                                {isCardEditable(card) ? (
                                  <Button type="button" size="auto" variant="ghost" onClick={() => openCardEditor(card)} aria-label={`Edit ${card.prompt}`} className="min-h-8 px-2.5 py-1.5 text-xs">
                                    <Pencil className="h-3.5 w-3.5" />Edit
                                  </Button>
                                ) : organizationId && !card.owner_organization ? (
                                  <Button type="button" size="auto" variant="ghost" onClick={() => void customizeCard(card)} aria-label={`Customize ${card.prompt}`} className="min-h-8 px-2.5 py-1.5 text-xs">
                                    <Copy className="h-3.5 w-3.5" />Customize
                                  </Button>
                                ) : null}
                                <Button type="button" size="icon" variant="ghost" aria-label="Move card up" disabled={index === 0} onClick={() => moveCard(index, -1)} className="h-8 w-8"><ArrowUp className="h-4 w-4" /></Button>
                                <Button type="button" size="icon" variant="ghost" aria-label="Move card down" disabled={index === orderedCards.length - 1} onClick={() => moveCard(index, 1)} className="h-8 w-8"><ArrowDown className="h-4 w-4" /></Button>
                                <Button type="button" size="icon" variant="ghost" aria-label={`Remove ${card.prompt} from deck`} onClick={() => removeCardFromDeck(card.id)} className="h-8 w-8 text-red-700"><X className="h-4 w-4" /></Button>
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card variant="white" maxWidth="none" disableAnimation className="border border-border/70 shadow-sm">
                  <CardContent variant="custom" className="px-6 py-16 text-center">
                    <Sparkles className="mx-auto h-7 w-7 text-muted-foreground" />
                    <p className="mt-3 font-semibold">Create or select a deck</p>
                    <p className="mt-1 text-sm text-muted-foreground">Deck settings and card ordering will appear here.</p>
                    <Button type="button" size="auto" onClick={createDeck} disabled={saving} className="mt-5 px-4 py-2"><Plus className="h-4 w-4" />New deck</Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )
      )}

      <Sheet open={cardLibraryOpen} onOpenChange={setCardLibraryOpen}>
        <SheetContent className="max-w-xl gap-0 p-0" aria-label="Inspire Card library">
          <div className="sticky top-0 z-10 border-b bg-white px-6 pb-5 pt-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <SheetTitle className="text-xl font-semibold">Add cards</SheetTitle>
                <SheetDescription className="mt-1 text-sm text-muted-foreground">Search the library for cards that are not already in {selectedDeck?.name || 'this deck'}.</SheetDescription>
              </div>
              <SheetClose asChild><Button type="button" size="icon" variant="ghost" aria-label="Close card library"><X className="h-4 w-4" /></Button></SheetClose>
            </div>
            <div className="relative mt-4">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input aria-label="Search card library" value={cardSearch} onChange={(event) => setCardSearch(event.target.value)} placeholder="Search questions…" className="pl-9" />
            </div>
          </div>
          <div className="space-y-3 p-6">
            <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
              <span>{availableCards.length} available {availableCards.length === 1 ? 'card' : 'cards'}</span>
              <Button type="button" size="auto" variant="ghost" onClick={openNewCard} className="min-h-8 px-2.5 py-1.5 text-xs"><Plus className="h-3.5 w-3.5" />Create new</Button>
            </div>
            {availableCards.length === 0 ? (
              <div className="rounded-xl border border-dashed px-5 py-10 text-center">
                <p className="text-sm font-semibold">No matching cards</p>
                <p className="mt-1 text-sm text-muted-foreground">Try another search or create a new card for this deck.</p>
              </div>
            ) : availableCards.map((card) => (
              <div key={card.id} className="flex items-start gap-3 rounded-xl border bg-white p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold leading-5">{card.prompt}</p>
                  <p className="mt-1 text-xs capitalize text-muted-foreground">{card.owner_organization?.name || 'WiseFriend'} · v{card.version} · {card.status}</p>
                </div>
                <Button type="button" size="auto" variant="secondary" onClick={() => addCardToDeck(card.id)} aria-label={`Add ${card.prompt} to deck`} className="min-h-8 px-3 py-1.5 text-xs"><Plus className="h-3.5 w-3.5" />Add</Button>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={cardEditorOpen} onOpenChange={setCardEditorOpen}>
        <SheetContent className="max-w-2xl gap-0 p-0" aria-label={cardDraft.id ? 'Edit Inspire Card' : 'Create Inspire Card'}>
          <form className="flex min-h-full flex-col" onSubmit={(event) => { event.preventDefault(); void saveCard(); }}>
            <div className="sticky top-0 z-10 border-b bg-white px-6 pb-5 pt-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <SheetTitle className="text-xl font-semibold">{cardDraft.id ? 'Edit card' : 'Create card'}</SheetTitle>
                    <Badge variant="outline" className="capitalize">{cardDraft.status}</Badge>
                  </div>
                  <SheetDescription className="mt-1 text-sm text-muted-foreground">
                    {cardDraft.id ? `Editing the ${editingCard?.owner_organization?.name || 'WiseFriend'} card.` : `The new card will be owned by ${brandingName} and added to this deck.`}
                  </SheetDescription>
                </div>
                <SheetClose asChild><Button type="button" size="icon" variant="ghost" aria-label="Close card editor"><X className="h-4 w-4" /></Button></SheetClose>
              </div>
            </div>
            <div className="flex-1 space-y-5 p-6">
              <div>
                <Label htmlFor="card-prompt">Prompt</Label>
                <Textarea id="card-prompt" className="mt-1 min-h-[132px] text-base" value={cardDraft.prompt} onChange={(event) => setCardDraft((current) => ({ ...current, prompt: event.target.value }))} maxLength={2000} placeholder="Ask one clear, open-ended question." />
                <p className="mt-1 text-right text-xs text-muted-foreground">{cardDraft.prompt.length.toLocaleString()}/2,000</p>
              </div>
              <div className="rounded-xl border bg-muted/20 p-4">
                <h3 className="text-sm font-semibold">Optional card-level overrides</h3>
                <p className="mt-1 text-xs text-muted-foreground">Leave either field blank to inherit the matching guidance from {selectedDeck?.name || 'the deck'}.</p>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="card-helper">Participant helper text</Label>
                    <Textarea id="card-helper" className="mt-1 min-h-[120px] bg-white" value={cardDraft.helper_text} onChange={(event) => setCardDraft((current) => ({ ...current, helper_text: event.target.value }))} placeholder={deckDraft.helper_text || 'Use the deck default'} />
                  </div>
                  <div>
                    <Label htmlFor="card-notes">Private facilitator notes</Label>
                    <Textarea id="card-notes" className="mt-1 min-h-[120px] bg-white" value={cardDraft.facilitator_notes} onChange={(event) => setCardDraft((current) => ({ ...current, facilitator_notes: event.target.value }))} placeholder={deckDraft.facilitator_notes || 'Use the deck default'} />
                  </div>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="card-status">Status</Label>
                  <select id="card-status" value={cardDraft.status} onChange={(event) => setCardDraft((current) => ({ ...current, status: event.target.value as typeof current.status }))} className={selectClassName}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="card-media">Image asset UUID <span className="font-normal text-muted-foreground">(optional)</span></Label>
                  <Input id="card-media" className="mt-1" value={cardDraft.media_asset} onChange={(event) => setCardDraft((current) => ({ ...current, media_asset: event.target.value }))} placeholder="Paste an existing media asset ID" />
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t bg-white px-6 py-4">
              <div>
                {editingCard && isCardEditable(editingCard) && (
                  <Button type="button" size="auto" variant="ghost" onClick={() => void archiveCard()} disabled={saving} className="min-h-9 px-3 py-2 text-red-700"><Archive className="h-4 w-4" />Archive card</Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" size="auto" variant="ghost" onClick={() => setCardEditorOpen(false)} className="min-h-9 px-4 py-2">Cancel</Button>
                <Button type="submit" size="auto" disabled={saving || !cardDraft.prompt.trim()} className="min-h-9 px-4 py-2"><Save className="h-4 w-4" />{saving ? 'Saving…' : cardDraft.id ? 'Save card' : 'Create card'}</Button>
              </div>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default InspireCardsAdmin;
